import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalstorageState } from 'rooks';

import { useAgentPanelContext } from './_context';
import { AGENT_MESSAGE_STATUSES, AGENT_PANEL_WIDTH, AGENT_STORAGE_KEYS } from './_enums';
import { applyStreamEvent, buildAgentContext, clampPanelWidth, emptyTurn } from './_helpers';

import { usePostAgentConversation, useRevalidateAgentConversation } from '@/data/agentConversation';
import { usePostAgentMessage } from '@/data/agentMessage';

// After Stop the server finishes the turn and saves it a moment later. Poll
// until it has, so the saved reply replaces the live one without a gap.
const SAVED_POLL_MS = 300;
const SAVED_POLL_TRIES = 10;

/**
 * Everything the panel holds, for AgentPanelProvider — and only for a user
 * with the feature: rooks writes each localStorage key on mount, so this is
 * never called for anyone else. Open, width and the current conversation are
 * kept in localStorage; the live turn and the page's context are not.
 */
export const useAgentPanelState = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useLocalstorageState(AGENT_STORAGE_KEYS.OPEN, false);
  const [storedWidth, setStoredWidth] = useLocalstorageState(AGENT_STORAGE_KEYS.WIDTH, AGENT_PANEL_WIDTH.DEFAULT);
  const [conversationUid, setConversationUid] = useLocalstorageState(AGENT_STORAGE_KEYS.CONVERSATION, null);
  const [pageContext, setPageContext] = useState({});
  // The chip leaves the context out of the next message on this page only.
  const [excludedPath, setExcludedPath] = useState(null);
  const [turn, setTurn] = useState(null);
  const abortRef = useRef(null);

  const postAgentConversation = usePostAgentConversation();
  const postAgentMessage = usePostAgentMessage();
  const revalidateAgentConversation = useRevalidateAgentConversation();

  const width = clampPanelWidth(storedWidth);
  const isRunning = turn?.status === AGENT_MESSAGE_STATUSES.RUNNING;

  // The app narrows by the panel's width instead of being covered by it: the
  // route container and right-hand sheets read --agent-w.
  useEffect(() => {
    document.documentElement.style.setProperty('--agent-w', isOpen ? `${width}px` : '0px');
    return () => document.documentElement.style.setProperty('--agent-w', '0px');
  }, [isOpen, width]);

  const context = useMemo(() => buildAgentContext(location.pathname, pageContext), [location.pathname, pageContext]);
  const isContextIncluded = excludedPath !== location.pathname;

  const waitForSaved = useCallback(
    async (uid, tries = SAVED_POLL_TRIES) => {
      const conversation = await revalidateAgentConversation(uid);
      if (!conversation?.is_running || tries <= 1) return;
      await new Promise((resolve) => setTimeout(resolve, SAVED_POLL_MS));
      await waitForSaved(uid, tries - 1);
    },
    [revalidateAgentConversation]
  );

  // A navigate block only renders a link: nothing here moves the user while a
  // reply streams.
  const handleStreamEvent = useCallback((event) => setTurn((current) => applyStreamEvent(current, event)), []);

  const send = useCallback(
    async (text) => {
      if (abortRef.current) return;
      const controller = new AbortController();
      abortRef.current = controller;
      const sentContext = isContextIncluded ? context : undefined;
      setTurn(emptyTurn(text));
      setExcludedPath(null);

      try {
        const uid = conversationUid ?? (await postAgentConversation({})).uid;
        if (!conversationUid) setConversationUid(uid);
        await postAgentMessage({ agent_conversation_uid: uid, text, ...(sentContext && { context: sentContext }) }, { signal: controller.signal, onEvent: handleStreamEvent });
        await waitForSaved(uid);
        setTurn(null);
      } catch (error) {
        setTurn((current) => ({ ...current, status: AGENT_MESSAGE_STATUSES.FAILED, error: error.message }));
      } finally {
        abortRef.current = null;
      }
    },
    [context, conversationUid, handleStreamEvent, isContextIncluded, postAgentConversation, postAgentMessage, setConversationUid, waitForSaved]
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const selectConversation = useCallback(
    (uid) => {
      setTurn(null);
      setConversationUid(uid);
    },
    [setConversationUid]
  );

  const open = useCallback(() => setIsOpen(true), [setIsOpen]);
  const close = useCallback(() => setIsOpen(false), [setIsOpen]);
  const toggle = useCallback(() => setIsOpen((current) => !current), [setIsOpen]);
  const setWidth = useCallback((value) => setStoredWidth(clampPanelWidth(value)), [setStoredWidth]);
  const startNewConversation = useCallback(() => selectConversation(null), [selectConversation]);
  const toggleContext = useCallback(() => setExcludedPath((current) => (current === location.pathname ? null : location.pathname)), [location.pathname]);
  const registerPageContext = useCallback((key, value) => setPageContext((current) => ({ ...current, [key]: value })), []);
  const unregisterPageContext = useCallback((key) => setPageContext((current) => Object.fromEntries(Object.entries(current).filter(([name]) => name !== key))), []);

  // Memoised so a page that reads the context re-renders when the panel
  // changes, not every time the provider does.
  return useMemo(
    () => ({
      isEnabled: true,
      isOpen,
      open,
      close,
      toggle,
      width,
      setWidth,
      conversationUid,
      selectConversation,
      startNewConversation,
      turn,
      isRunning,
      send,
      stop,
      context,
      isContextIncluded,
      toggleContext,
      registerPageContext,
      unregisterPageContext
    }),
    [
      isOpen,
      open,
      close,
      toggle,
      width,
      setWidth,
      conversationUid,
      selectConversation,
      startNewConversation,
      turn,
      isRunning,
      send,
      stop,
      context,
      isContextIncluded,
      toggleContext,
      registerPageContext,
      unregisterPageContext
    ]
  );
};

/**
 * Adds a page's own state to what the assistant sees with each message.
 * `label` is special: it names the page in the "On: …" chip; anything else
 * goes to the API as `context.extra`, a flat map of short values. Cleared when
 * the page unmounts. A no-op for a user without the panel.
 */
export const useAgentContext = (key, value) => {
  const { registerPageContext, unregisterPageContext } = useAgentPanelContext();
  // Compared by content, so a page passing a fresh object each render does
  // not re-register it — which would re-render the page, and loop.
  const serialised = JSON.stringify(value ?? null);

  useEffect(() => {
    if (!registerPageContext) return undefined;
    registerPageContext(key, JSON.parse(serialised));
    return () => unregisterPageContext(key);
  }, [key, serialised, registerPageContext, unregisterPageContext]);
};

/** ⌘J (Ctrl+J elsewhere) opens and closes the panel from anywhere. */
export const useToggleAgentPanelShortcut = (toggle) => {
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        toggleRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
};

/**
 * Dragging the panel's left edge. Returns the handle's pointer-down handler;
 * the width follows the pointer until it is released.
 */
export const useAgentPanelResize = (setWidth) => {
  const handlePointerDown = (event) => {
    event.preventDefault();
    const handleMove = (moveEvent) => setWidth(window.innerWidth - moveEvent.clientX);
    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      document.body.style.removeProperty('user-select');
    };
    document.body.style.setProperty('user-select', 'none');
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  return handlePointerDown;
};
